import { createSelector } from "@reduxjs/toolkit";
import type { IRootState } from "../app/store";
import { openapi } from "../../openapi";
import { selectActiveRoomColoring } from "../Room/roomSelectors";
import type { AgCartesianChartOptions } from "ag-charts-enterprise";
import { selectTrendByKeyArgsForRoomColoring } from "./roomColoringSelectors";

const FALLBACK_GRAY = "#9aa0a6";

type ITrendPoint = {
  date: string | Date;
  total: number;
  series: { value: any; count: number }[];
};

function toIsoDay(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function stableKey(v: any): string {
  if (v === null) return "null";
  if (v === undefined) return "undefined";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v);
}

// "2025-12-01" -> "12-01"
function shortDateLabel(isoDate: string): string {
  if (!isoDate || isoDate.length < 10) return isoDate;
  return isoDate.slice(5);
}

export const selectHttpTrendByKeyRoomSnapshotData = createSelector(
  [(state: IRootState) => state, selectTrendByKeyArgsForRoomColoring],
  (state, args) => {
    if (!args) return null;

    // ✅ ensure this matches your generated endpoint name
    return openapi.endpoints.postMultiplacerRoomSnapshotTrendByKey.select(args)(state).data ?? null;
  }
);

export type IRoomColoringTrendChartSelectorResult = {
  isEmpty: boolean;
  chartData: Array<Record<string, any>>;
  seriesKeys: string[];
  colorByKey: Map<string, string>;
  options: AgCartesianChartOptions;
};

export const selectRoomColoringTrendChart = createSelector(
  [selectHttpTrendByKeyRoomSnapshotData, selectActiveRoomColoring],
  (data, roomColoring): IRoomColoringTrendChartSelectorResult => {
    const points = (data ?? []) as unknown as ITrendPoint[];

    const specs = roomColoring?.colorSpecs ?? [];
    const specIds = specs.map((s) => s.id);

    const colorByKey = new Map<string, string>();
    for (const s of specs) {
      colorByKey.set(s.id, (s.fillColor as string) ?? FALLBACK_GRAY);
    }

    const dataKeysSet = new Set<string>();
    for (const p of points) {
      for (const s of p.series ?? []) {
        dataKeysSet.add(stableKey(s.value));
      }
    }

    const extraKeys = [...dataKeysSet].filter((k) => !specIds.includes(k));
    extraKeys.sort((a, b) => a.localeCompare(b));

    const seriesKeys = [...specIds, ...extraKeys];

    for (const k of seriesKeys) {
      if (!colorByKey.has(k)) colorByKey.set(k, FALLBACK_GRAY);
    }

    const chartData = points.map((p) => {
      const row: Record<string, any> = { date: toIsoDay(p.date) };
      for (const k of seriesKeys) row[k] = 0;

      for (const s of p.series ?? []) {
        const k = stableKey(s.value);
        if (k in row) row[k] = s.count;
      }
      return row;
    });

    const isEmpty = chartData.length === 0;

    const options: AgCartesianChartOptions = {
      data: chartData,

      background: { fill: "transparent" },
      height: 210,
      legend: { enabled: false },

      padding: { top: 10, right: 18, bottom: 14, left: 10 },

      axes: [
        {
          type: "category",
          position: "bottom",
          label: {
            fontSize: 11,
            formatter: (p) => shortDateLabel(String(p.value)),
            avoidCollisions: true,
            autoRotate: true,
          },
        },
        {
          type: "number",
          position: "left",
          label: { fontSize: 11 },
          gridLine: { enabled: true },
        },
      ],

      series: seriesKeys.map((key) => ({
        type: "area",
        xKey: "date",
        yKey: key,
        yName: key,
        stacked: true,
        fill: colorByKey.get(key) ?? FALLBACK_GRAY,
        stroke: colorByKey.get(key) ?? FALLBACK_GRAY,
        strokeWidth: 1,
        marker: { enabled: false },
      })),

      tooltip: { enabled: true },
    };

    return { isEmpty, chartData, seriesKeys, colorByKey, options };
  }
);
